import { useEffect, useState } from "react";
import { WITH_COOKIE } from "../tokenStorage";

interface OrderFile {
  id: string;
  filename: string;
  byte_size: number;
  /** "rendering" for the checkout email's image, null for a design file. */
  kind: string | null;
}

/** A custom digitizing order, as app/routers/admin.py reports it. */
interface Order {
  id: string;
  customer_email: string | null;
  item_name: string;
  amount_cents: number;
  email_subject: string;
  /** What the customer is sent: /pay/<id>, which makes Stripe sessions as
   *  needed. Never the Stripe URL, which dies within a day. */
  pay_url: string;
  /** When the quote stops being payable, with its UTC offset. */
  expires_at: string;
  quote_state: "open" | "expired" | "cancelled" | "paid";
  status: "awaiting_payment" | "paid" | "emailed" | "email_failed" | "cancelled";
  created_at: string;
  paid_at: string | null;
  emailed_to: string | null;
  emailed_at: string | null;
  error: string | null;
  checkout_email_sent_at: string | null;
  checkout_email_error: string | null;
  stitch_preview_url: string | null;
  files: OrderFile[];
}

interface AdminPageProps {
  apiUrl: string;
  /** This browser's token, or "" until App has resolved it. The cookie
   *  carries it too; the body copy covers a token not yet in the cookie. */
  token: string;
  tokenResolved: boolean;
}

const STATUS_LABELS: Record<Order["status"], string> = {
  awaiting_payment: "Awaiting payment",
  paid: "Paid, sending",
  emailed: "Paid and emailed",
  email_failed: "Paid, email failed",
  cancelled: "Cancelled",
};

/** The status cell's headline: an unpaid quote's state comes from its
 *  expiry, which the stored status can't know. */
function statusLabel(order: Order): string {
  if (order.quote_state === "expired") return "Quote expired";
  return STATUS_LABELS[order.status] ?? order.status;
}

// The order list's filter buttons, by quote state. Paid covers delivered,
// sending and failed alike; a failed delivery is highlighted instead.
type OrderFilter = "all" | Order["quote_state"];
const FILTERS: { key: OrderFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "open", label: "Awaiting payment" },
  { key: "paid", label: "Paid" },
  { key: "expired", label: "Expired" },
  { key: "cancelled", label: "Cancelled" },
];

type OrderSort = "newest" | "oldest" | "expiring" | "price-high" | "price-low";
const SORTS: { key: OrderSort; label: string }[] = [
  { key: "newest", label: "Newest first" },
  { key: "oldest", label: "Oldest first" },
  { key: "expiring", label: "Expiring soonest" },
  { key: "price-high", label: "Price, high to low" },
  { key: "price-low", label: "Price, low to high" },
];

const time = (iso: string) => new Date(iso).getTime();

export function sortOrders(orders: Order[], sort: OrderSort): Order[] {
  const sorted = [...orders];
  switch (sort) {
    case "newest":
      return sorted.sort((a, b) => time(b.created_at) - time(a.created_at));
    case "oldest":
      return sorted.sort((a, b) => time(a.created_at) - time(b.created_at));
    case "expiring":
      // Open quotes by deadline first; everything else after, newest first,
      // since its deadline no longer means anything.
      return sorted.sort((a, b) => {
        const aOpen = a.quote_state === "open", bOpen = b.quote_state === "open";
        if (aOpen !== bOpen) return aOpen ? -1 : 1;
        return aOpen ? time(a.expires_at) - time(b.expires_at) : time(b.created_at) - time(a.created_at);
      });
    case "price-high":
      return sorted.sort((a, b) => b.amount_cents - a.amount_cents);
    case "price-low":
      return sorted.sort((a, b) => a.amount_cents - b.amount_cents);
  }
}

// An open quote this close to its deadline is highlighted.
const EXPIRING_SOON_MS = 2 * 24 * 60 * 60 * 1000;

export function rowClass(order: Order): string | undefined {
  if (order.status === "email_failed") return "order-row-failed";
  if (order.quote_state === "open" && time(order.expires_at) - Date.now() < EXPIRING_SOON_MS) {
    return "order-row-expiring";
  }
  if (order.quote_state === "expired" || order.quote_state === "cancelled") return "order-row-inactive";
  return undefined;
}

export function badgeClass(order: Order): string {
  if (order.status === "email_failed") return "order-badge order-badge-failed";
  return `order-badge order-badge-${order.quote_state}`;
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

const DEFAULT_BODY = `Hi,

Thanks for your order. Your custom digitized design is attached.

Any questions, just reply to this email.`;

// Must match CHECKOUT_LINK in app/services/email.py, which swaps it for the
// link and refuses a checkout email without it.
const CHECKOUT_LINK = "{{checkout_link}}";
// Likewise STITCH_PREVIEW, swapped for the stitch preview link in either email.
const STITCH_PREVIEW = "{{stitch_preview}}";

/** Why the order can't be sent as is, checked here too so the form says so
 *  before a round trip. The server checks the same and is what counts. */
function orderProblem(
  files: File[],
  preview: string,
  body: string,
  checkoutBody: string | null,
  rendering: File | null,
  allowNoRendering: boolean,
): string | null {
  const link = preview.trim();
  if (checkoutBody !== null && !rendering && !allowNoRendering) {
    return "Add a preview image for the pay page, or tick Send without a preview.";
  }
  if (files.length === 0 && !link) {
    return "Attach the design or link a stitch preview: the delivery email would have nothing in it.";
  }
  if (link && !body.includes(STITCH_PREVIEW)) {
    return `Put ${STITCH_PREVIEW} in the delivery email, or clear the stitch preview link.`;
  }
  if (!link && (body.includes(STITCH_PREVIEW) || (checkoutBody ?? "").includes(STITCH_PREVIEW))) {
    return `The copy uses ${STITCH_PREVIEW} but no stitch preview is linked.`;
  }
  return null;
}

const DEFAULT_CHECKOUT_BODY = `Hi,

Your custom digitized design is ready. You can see a preview of it, and
pay for it, here any time in the next 15 days:
${CHECKOUT_LINK}

As soon as the payment goes through, we'll email you the embroidery files.`;

/** Links between the admin pages. Plain page loads: each page checks
 *  access for itself on mount. */
export function AdminNav({ current }: { current: "orders" | "email" | "campaigns" | "media" | "templates" }) {
  return (
    <nav>
      <ul>
        <li>{current === "orders" ? <strong>Orders</strong> : <a href="/admin">Orders</a>}</li>
        <li>
          {current === "email" ? <strong>Send an email</strong> : <a href="/admin/email">Send an email</a>}
        </li>
        <li>
          {current === "campaigns" ? <strong>Campaigns</strong> : <a href="/admin/campaigns">Campaigns</a>}
        </li>
        <li>{current === "media" ? <strong>Media</strong> : <a href="/admin/media">Media</a>}</li>
        <li>
          {current === "templates" ? <strong>Templates</strong> : <a href="/admin/templates">Templates</a>}
        </li>
      </ul>
    </nav>
  );
}

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

/** Errors come back as FastAPI's {detail}, or not at all from a proxy. */
async function failure(res: Response): Promise<string> {
  try {
    const body = await res.json();
    if (typeof body.detail === "string") return body.detail;
  } catch {
    // Not JSON: fall through to the status.
  }
  return `HTTP ${res.status}`;
}

/**
 * Custom digitizing orders: upload the finished design, write the email that
 * delivers it, and get a Stripe checkout link for a price set here. The
 * backend sends the email when Stripe reports the checkout paid.
 *
 * The same bundle ships to everyone; what keeps this page to the admin is
 * the API, which answers 404 to any other account. That renders here as
 * "not available", the same as the dev flags page off dev.
 */
export function AdminPage({ apiUrl, token, tokenResolved }: AdminPageProps) {
  const [access, setAccess] = useState<"checking" | "denied" | "granted">("checking");
  const [stripeReady, setStripeReady] = useState(false);
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<Order | null>(null);
  const [filter, setFilter] = useState<OrderFilter>("all");
  const [sort, setSort] = useState<OrderSort>("newest");
  const [copied, setCopied] = useState(false);

  const [customerEmail, setCustomerEmail] = useState("");
  const [price, setPrice] = useState("");
  const [itemName, setItemName] = useState("Custom digitizing");
  const [subject, setSubject] = useState("Your custom digitized design");
  const [body, setBody] = useState(DEFAULT_BODY);
  const [files, setFiles] = useState<File[]>([]);
  const [stitchPreview, setStitchPreview] = useState("");
  const [sendCheckoutEmail, setSendCheckoutEmail] = useState(false);
  const [checkoutSubject, setCheckoutSubject] = useState("Your custom digitized design is ready");
  const [checkoutBody, setCheckoutBody] = useState(DEFAULT_CHECKOUT_BODY);
  const [rendering, setRendering] = useState<File | null>(null);
  const [allowNoRendering, setAllowNoRendering] = useState(false);
  // Bumped to clear the file input, which React can't set a value on.
  const [fileInputKey, setFileInputKey] = useState(0);

  const post = (path: string, payload: object = {}) =>
    fetch(`${apiUrl}${path}`, {
      ...WITH_COOKIE,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: token || null, ...payload }),
    });

  const loadOrders = async () => {
    const res = await post("/api/admin/orders/list");
    if (res.ok) setOrders(await res.json());
    else setError(`Could not load orders: ${await failure(res)}`);
  };

  useEffect(() => {
    if (!tokenResolved) return;
    (async () => {
      try {
        const res = await post("/api/admin/me");
        if (!res.ok) {
          setAccess("denied");
          return;
        }
        setStripeReady((await res.json()).stripe === true);
        setAccess("granted");
        await loadOrders();
      } catch (err) {
        setError(`Could not reach the server: ${err}`);
        setAccess("denied");
      }
    })();
  }, [tokenResolved, token]);

  const createOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    const problem = orderProblem(files, stitchPreview, body, sendCheckoutEmail ? checkoutBody : null, rendering, allowNoRendering);
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    setCreated(null);
    setCopied(false);
    try {
      const form = new FormData();
      if (token) form.append("token", token);
      form.append("customer_email", customerEmail);
      form.append("price", price);
      form.append("item_name", itemName);
      form.append("email_subject", subject);
      form.append("email_body", body);
      for (const file of files) form.append("files", file);
      form.append("stitch_preview_url", stitchPreview);
      if (sendCheckoutEmail) {
        form.append("checkout_email_subject", checkoutSubject);
        form.append("checkout_email_body", checkoutBody);
        if (allowNoRendering) form.append("allow_no_rendering", "true");
      }
      if (rendering) form.append("rendering", rendering);
      const res = await fetch(`${apiUrl}/api/admin/orders`, {
        ...WITH_COOKIE,
        method: "POST",
        body: form,
      });
      if (!res.ok) {
        setError(await failure(res));
        return;
      }
      const order: Order = await res.json();
      setCreated(order);
      setOrders((current) => [order, ...(current ?? [])]);
      // Keep the email copy and item name: the next order usually reuses
      // them. The customer, price and files are this order's alone.
      setCustomerEmail("");
      setPrice("");
      setFiles([]);
      setStitchPreview("");
      setRendering(null);
      setAllowNoRendering(false);
      setFileInputKey((k) => k + 1);
    } catch (err) {
      setError(`Could not create the order: ${err}`);
    } finally {
      setBusy(false);
    }
  };

  // Cancel and extend answer with the updated order, swapped in place.
  const changeQuote = async (order: Order, action: "cancel" | "extend") => {
    setBusy(true);
    setError(null);
    try {
      const res = await post(`/api/admin/orders/${order.id}/${action}`);
      if (!res.ok) {
        setError(await failure(res));
        await loadOrders();
        return;
      }
      const updated: Order = await res.json();
      setOrders((current) => (current ?? []).map((o) => (o.id === updated.id ? updated : o)));
    } finally {
      setBusy(false);
    }
  };

  const resend = async (order: Order) => {
    setBusy(true);
    setError(null);
    try {
      const res = await post(`/api/admin/orders/${order.id}/send`);
      if (!res.ok) setError(await failure(res));
      await loadOrders();
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      // Clipboard refused (permissions, insecure origin): the link is on
      // screen to copy by hand.
    }
  };

  const visibleOrders = sortOrders(
    (orders ?? []).filter((o) => filter === "all" || o.quote_state === filter),
    sort,
  );

  if (access === "checking") return <p aria-busy="true">Loading…</p>;

  if (access === "denied") {
    return (
      <article>
        <h2>Admin</h2>
        <p>This page isn't available.</p>
      </article>
    );
  }

  return (
    <>
      <AdminNav current="orders" />
      <article>
        <h2>New custom digitizing order</h2>
        {!stripeReady && (
          <p style={{ color: "var(--pico-del-color, #b3261e)" }}>
            Stripe isn't configured on this server (STRIPE_SECRET_KEY), so
            orders can't be created yet.
          </p>
        )}
        <form onSubmit={createOrder}>
          <label>
            Design files
            <input
              key={fileInputKey}
              type="file"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
            />
            <small>Attached to the delivery email. PES, DST, a PDF proof, whatever the customer needs.</small>
          </label>

          <label>
            Stitch preview link
            <input
              type="url"
              placeholder="https://embroiderytext.com/replay/..."
              value={stitchPreview}
              onChange={(e) => setStitchPreview(e.target.value)}
            />
            <small>
              Fills in <code>{STITCH_PREVIEW}</code> in either email. Attach
              files, link a preview, or both.
            </small>
          </label>

          <label>
            Preview image
            <input
              key={fileInputKey}
              type="file"
              accept="image/*"
              required={sendCheckoutEmail && !allowNoRendering}
              onChange={(e) => setRendering(e.target.files?.[0] ?? null)}
            />
            <small>
              Shown to the customer on the pay page, where we can see whether
              they looked at it. Not attached to any email.
              {sendCheckoutEmail && !allowNoRendering ? " Required with a checkout email." : ""}
            </small>
          </label>

          <div className="grid">
            <label>
              Price (USD)
              <input
                type="text"
                inputMode="decimal"
                placeholder="45.00"
                required
                value={price}
                onChange={(e) => setPrice(e.target.value)}
              />
            </label>
            <label>
              Item name
              <input
                type="text"
                required
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
              />
            </label>
          </div>

          <label>
            Customer email
            <input
              type="email"
              placeholder="Optional"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
            />
            <small>
              Prefilled at checkout. Left blank, the design goes to whatever
              address the customer gives Stripe.
            </small>
          </label>

          <h3>Checkout email</h3>
          <label>
            <input
              type="checkbox"
              checked={sendCheckoutEmail}
              onChange={(e) => setSendCheckoutEmail(e.target.checked)}
            />
            Email the customer the checkout link now
          </label>
          {sendCheckoutEmail && (
            <>
              <label>
                Subject
                <input
                  type="text"
                  required
                  value={checkoutSubject}
                  onChange={(e) => setCheckoutSubject(e.target.value)}
                />
              </label>
              <label>
                Body
                <textarea
                  rows={8}
                  required
                  value={checkoutBody}
                  onChange={(e) => setCheckoutBody(e.target.value)}
                  aria-invalid={checkoutBody.includes(CHECKOUT_LINK) ? undefined : true}
                />
                <small>
                  Plain text. <code>{CHECKOUT_LINK}</code> is replaced with the
                  Stripe link{checkoutBody.includes(CHECKOUT_LINK) ? "." : ", and has to be in here."}
                  {" "}<code>{STITCH_PREVIEW}</code> works here too. Needs a
                  customer email above.
                </small>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={allowNoRendering}
                  onChange={(e) => setAllowNoRendering(e.target.checked)}
                />
                Send without a preview image
              </label>
            </>
          )}

          <h3>Delivery email</h3>
          <p><small>Sent once the checkout is paid, with the design files attached.</small></p>
          <label>
            Email subject
            <input
              type="text"
              required
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </label>

          <label>
            Email body
            <textarea
              rows={8}
              required
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <small>
              Plain text.{" "}
              {stitchPreview.trim()
                ? <><code>{STITCH_PREVIEW}</code> is replaced with the stitch preview link{body.includes(STITCH_PREVIEW) ? "." : ", and has to be in here."}</>
                : <>With a stitch preview linked above, <code>{STITCH_PREVIEW}</code> goes in here.</>}
            </small>
          </label>

          <button type="submit" disabled={busy || !stripeReady} aria-busy={busy}>
            Create checkout link
          </button>
        </form>

        {error && <p style={{ color: "var(--pico-del-color, #b3261e)" }}>{error}</p>}

        {created?.checkout_email_sent_at && (
          <p>Checkout email sent to {created.customer_email}.</p>
        )}
        {created?.checkout_email_error && (
          <p style={{ color: "var(--pico-del-color, #b3261e)" }}>
            The checkout email failed ({created.checkout_email_error}). Send
            the link below by hand.
          </p>
        )}
        {created && (
          <div>
            <p>
              Payment link for {dollars(created.amount_cents)}, good until{" "}
              {shortDate(created.expires_at)}. Send it to the customer:
            </p>
            <div role="group">
              <input type="text" readOnly value={created.pay_url} onFocus={(e) => e.target.select()} />
              <button type="button" onClick={() => copyLink(created.pay_url)}>
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
          </div>
        )}
      </article>

      <article>
        <h2>Orders</h2>
        {orders === null && <p aria-busy="true">Loading…</p>}
        {orders?.length === 0 && <p>No orders yet.</p>}
        {orders && orders.length > 0 && (
          <div className="order-filters" role="group" aria-label="Filter orders">
            {FILTERS.map(({ key, label }) => {
              const count = key === "all" ? orders.length : orders.filter((o) => o.quote_state === key).length;
              return (
                <button
                  key={key}
                  type="button"
                  className={filter === key ? undefined : "outline"}
                  aria-pressed={filter === key}
                  onClick={() => setFilter(key)}
                >
                  {label} ({count})
                </button>
              );
            })}
            <select value={sort} onChange={(e) => setSort(e.target.value as OrderSort)} aria-label="Sort orders">
              {SORTS.map(({ key, label }) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        )}
        {orders && orders.length > 0 && visibleOrders.length === 0 && (
          <p>No {FILTERS.find((f) => f.key === filter)?.label.toLowerCase()} orders.</p>
        )}
        {orders && visibleOrders.length > 0 && (
          <div className="overflow-auto">
            <table className="admin-orders">
              <thead>
                <tr>
                  <th scope="col">Created</th>
                  <th scope="col">Customer</th>
                  <th scope="col">Item</th>
                  <th scope="col">Price</th>
                  <th scope="col">Status</th>
                  <th scope="col"></th>
                </tr>
              </thead>
              <tbody>
                {visibleOrders.map((order) => (
                  <tr key={order.id} className={rowClass(order)}>
                    <td>{new Date(order.created_at).toLocaleString()}</td>
                    <td>{order.emailed_to || order.customer_email || <small>(from checkout)</small>}</td>
                    <td>
                      {order.item_name}
                      <br />
                      {/* Plain links: the download route takes the login
                          cookie, so the token never goes in a URL. */}
                      {order.files.map((f) => (
                        <small key={f.id}>
                          <br />
                          <a href={`${apiUrl}/api/admin/orders/${order.id}/files/${f.id}`}>{f.filename}</a>
                          {f.kind === "rendering" && " (preview image)"}
                        </small>
                      ))}
                      {order.stitch_preview_url && (
                        <small>
                          <br />
                          <a href={order.stitch_preview_url} target="_blank" rel="noreferrer">
                            Stitch preview
                          </a>
                        </small>
                      )}
                    </td>
                    <td>{dollars(order.amount_cents)}</td>
                    <td>
                      <span className={badgeClass(order)}>{statusLabel(order)}</span>
                      {(order.quote_state === "open" || order.quote_state === "expired") && (
                        <>
                          <br />
                          <small>
                            {order.quote_state === "open" ? "Payable until " : "Expired "}
                            {shortDate(order.expires_at)}
                          </small>
                        </>
                      )}
                      {order.checkout_email_sent_at && (
                        <>
                          <br />
                          <small>Checkout link emailed</small>
                        </>
                      )}
                      {order.checkout_email_error && (
                        <>
                          <br />
                          <small>Checkout email failed: {order.checkout_email_error}</small>
                        </>
                      )}
                      {order.error && (
                        <>
                          <br />
                          <small>{order.error}</small>
                        </>
                      )}
                    </td>
                    <td>
                      {order.quote_state === "open" && (
                        <>
                          <a href="#" onClick={(e) => { e.preventDefault(); copyLink(order.pay_url); }}>
                            Copy link
                          </a>
                          <br />
                          <a href="#" onClick={(e) => { e.preventDefault(); if (!busy) changeQuote(order, "cancel"); }}>
                            Cancel
                          </a>
                          <br />
                        </>
                      )}
                      {order.quote_state !== "paid" && (
                        <a href="#" onClick={(e) => { e.preventDefault(); if (!busy) changeQuote(order, "extend"); }}>
                          {order.quote_state === "open" ? "Extend 15 days" : "Reopen for 15 days"}
                        </a>
                      )}
                      {order.paid_at && (
                        <a href="#" onClick={(e) => { e.preventDefault(); if (!busy) resend(order); }}>
                          {order.status === "emailed" ? "Resend" : "Retry email"}
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </article>
    </>
  );
}
